import { useQuery } from '@tanstack/react-query'

import { type AppTimezonePreference } from '@sparkai/utils'

import { apiClient } from '../../apiClient'
import { getSelectedTimezoneHeaders, SPARK_SELECTED_TIMEZONE_HEADER } from '../../timezoneRequestContext'

export type ProgramInstanceSpecialistsOrderBy =
  | 'name'
  | 'taskCount'
  | 'liveAvgAccuracy'
  | 'liveAccuracyTrend'
  | 'recycledAvgAccuracy'
  | 'recycledAccuracyTrend'
  | 'auditedCount'
  | 'avgTimePerTask'
  | 'avgTimePerTaskTrend'
  | 'avgAnnotationPerTask'
  | 'avgTimePerAnnotation'
  | 'avgTimePerAnnotationTrend'

/**
 * One Mission Specialist across the Live program and its linked Recycler. Accuracies are 0–1 ratios,
 * times are milliseconds and trends are percent changes; `null` means the period has no samples.
 */
export type ProgramInstanceSpecialistRow = {
  operatorId: string
  name: string | null
  email: string | null
  avatarUrl: string | null
  taskCount: number
  auditedCount: number
  liveAuditedCount: number
  recycledAuditedCount: number
  liveAvgAccuracy: number | null
  liveAccuracyTrend: number | null
  recycledAvgAccuracy: number | null
  recycledAccuracyTrend: number | null
  avgTimePerTask: number | null
  avgTimePerTaskTrend: number | null
  avgAnnotationPerTask: number | null
  avgTimePerAnnotation: number | null
  avgTimePerAnnotationTrend: number | null
}

export type ProgramInstanceSpecialistsResponse = {
  programInstanceId: string
  recyclerProgramId: string | null
  total: number
  breakdown: ProgramInstanceSpecialistRow[]
}

type GetProgramInstanceSpecialistsRequest = {
  programInstanceId?: string
  startDate?: string
  endDate?: string
  cursor: number
  limit: number
  orderBy: ProgramInstanceSpecialistsOrderBy
  sortDescending: boolean
  timezonePreference?: AppTimezonePreference
}

export const PROGRAM_MISSION_SPECIALIST_REPORT_KEY = 'program-mission-specialist-report'

export function useGetReport({
  programInstanceId,
  timezonePreference,
  ...params
}: GetProgramInstanceSpecialistsRequest) {
  const timezoneHeaders = timezonePreference ? getSelectedTimezoneHeaders(timezonePreference) : undefined

  return useQuery({
    queryKey: [
      PROGRAM_MISSION_SPECIALIST_REPORT_KEY,
      programInstanceId,
      params,
      timezoneHeaders?.[SPARK_SELECTED_TIMEZONE_HEADER],
    ],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramInstanceSpecialistsResponse>(
        `/int/v3/program-instances/${programInstanceId}/reports/specialists`,
        {
          params,
          ...(timezoneHeaders ? { headers: timezoneHeaders } : {}),
        }
      )

      return data
    },
    enabled: Boolean(programInstanceId) && Boolean(params.startDate) && Boolean(params.endDate),
  })
}
